import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { DocumentType, DocumentTypeDetectionConfig } from '../../models/DocumentType'

import { paginate } from '../utils'

const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface GetDocumentTypesRequest extends BasePageRequest {
  includeInactive?: boolean
}

export interface GetAllDocumentTypesRequest extends BaseRequest {
  includeInactive?: boolean
}

export interface GetDocumentTypeByIdRequest extends BaseRequest {
  documentTypeId: string
}

export interface CreateDocumentTypePayload {
  name: string
  isActive?: boolean
  detectionConfig: DocumentTypeDetectionConfig
}

export interface CreateDocumentTypeRequest extends BaseRequest {
  payload: CreateDocumentTypePayload
}

export interface UpdateDocumentTypePayload {
  name?: string
  isActive?: boolean
  detectionConfig?: DocumentTypeDetectionConfig
}

export interface UpdateDocumentTypeRequest extends BaseRequest {
  documentTypeId: string
  payload: UpdateDocumentTypePayload
}

export interface DeleteDocumentTypeRequest extends BaseRequest {
  documentTypeId: string
}

export const documentTypeApi = createApi({
  reducerPath: 'documentTypeApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['DocumentType'],
  endpoints: (builder) => ({
    getDocumentTypes: builder.query<BasePageResponse<DocumentType[]>, GetDocumentTypesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/document-types`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            includeInactive: request.includeInactive,
          },
        }
      },
      providesTags: ['DocumentType'],
    }),
    getAllDocumentTypes: builder.query<BaseResponse<DocumentType[]>, GetAllDocumentTypesRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        return await paginate<DocumentType>(
          {
            url: `/accounts/${request.accountId}/document-types`,
            params: {
              includeInactive: request.includeInactive,
            },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['DocumentType'],
    }),
    getDocumentTypeById: builder.query<BaseResponse<DocumentType>, GetDocumentTypeByIdRequest>({
      query: (request) => `/accounts/${request.accountId}/document-types/${request.documentTypeId}`,
      providesTags: ['DocumentType'],
    }),
    createDocumentType: builder.mutation<DocumentType, CreateDocumentTypeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/document-types/`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<DocumentType>) => {
        return response.data
      },
      invalidatesTags: ['DocumentType'],
    }),
    updateDocumentType: builder.mutation<void, UpdateDocumentTypeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/document-types/${request.documentTypeId}`,
        method: 'PATCH',
        body: request.payload,
      }),
      invalidatesTags: ['DocumentType'],
    }),
    deleteDocumentType: builder.mutation<void, DeleteDocumentTypeRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/document-types/${request.documentTypeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DocumentType'],
    }),
  }),
})

export const {
  useGetDocumentTypesQuery,
  useLazyGetAllDocumentTypesQuery,
  useGetAllDocumentTypesQuery,
  useGetDocumentTypeByIdQuery,
  useLazyGetDocumentTypeByIdQuery,
  useCreateDocumentTypeMutation,
  useUpdateDocumentTypeMutation,
  useDeleteDocumentTypeMutation,
} = documentTypeApi
